import { describe, it, expect } from '@jest/globals';
import { createPackageDefinitions } from './createPackageDefinitions.js';
import { createTestFileStructure } from '@ms-cloudpack/test-utilities';
import type { PackageJson } from '@ms-cloudpack/common-types';
import { readConfig } from '../readConfig/readConfig.js';
import { writeAppConfig } from '../writeConfig/writeAppConfig.js';
import { writeGeneratedConfig } from '../writeConfig/writeGeneratedConfig.js';
import path from 'path';

/**
 * This should only include basic tests for the combined functionality.
 * Detailed tests for `createPackageSettingsTransform` logic go in that test file.
 */
describe('createPackageDefinitions', () => {
  it('can change dependencies using include/exclude dependencies', async () => {
    const originalDefinition: PackageJson & Record<string, unknown> = {
      name: 'foo',
      version: '17.0.0',
      private: true,
      scripts: {},
      dependencies: {
        foo: '1.0.0',
        bar: '1.0.0',
      },
      devDependencies: {
        baz: '1.0.0',
        boz: '1.0.0',
      },
    };
    const packagePath = await createTestFileStructure({
      'package.json': originalDefinition,
    });
    await writeAppConfig(
      {
        packageSettings: [
          {
            match: { name: 'foo' },
            includedDependencies: ['baz'],
            excludedDependencies: ['foo'],
          },
        ],
      },
      packagePath,
    );
    await writeGeneratedConfig(
      {
        packageSettings: [
          {
            match: { name: 'foo', version: '^17' },
            includedDependencies: ['boz'],
            excludedDependencies: ['bar'],
          },
        ],
      },
      packagePath,
    );

    const config = await readConfig(packagePath);
    const packages = createPackageDefinitions({ config });
    const definition = await packages.get(packagePath);

    // Test the whole definition.
    // (The extra property removal is done in the pre-transform copy step by PackageDefinitions.)
    expect(definition).toEqual({
      name: 'foo',
      version: '17.0.0',
      exports: {},
      // dependencies updated
      dependencies: { baz: '1.0.0' },
      // peer dependencies updated
      peerDependencies: { boz: '1.0.0' },
      // original dev deps preserved
      devDependencies: originalDefinition.devDependencies,
      cloudpack: { mode: 'library' },
    });
  });

  it('applies built-in settings', async () => {
    const testPath = await createTestFileStructure({
      'node_modules/react/package.json': {
        name: 'react',
        version: '17.0.0',
      },
    });

    const config = await readConfig(testPath);
    const packages = createPackageDefinitions({ config });

    const definition = await packages.get(path.join(testPath, 'node_modules/react'));
    expect(definition).toEqual({
      name: 'react',
      version: '17.0.0',
      exports: {
        '.': './index.js',
        './jsx-dev-runtime': './jsx-dev-runtime.js',
        './jsx-runtime': './jsx-runtime.js',
      },
      cloudpack: {
        mode: 'library',
      },
    } satisfies PackageJson);
  });
});
